// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductActions, { ProductActionsStyle } from "@pace/App/Components/Product/ProductActions";
import Clickable, { ClickablePresentationProps } from "@pace/MobiusOverrides/Clickable/Clickable";
import React from "react";
import { css } from "styled-components";

interface Props {
    // products: ProductAutocompleteItemModel[];
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;
    // XNG-Changes
    products: ProductContextModel[];
}

export interface AutocompleteProductsStyles {
    headerText?: TypographyPresentationProps;
    container?: GridContainerProps;
    focusedContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    image?: LazyImageProps;
    infoGridItem?: GridItemProps;
    titleLink?: LinkPresentationProps;
    erpNumberText?: SmallHeadingAndTextStyles;
    // XNG-Changes
    imageClickable?: ClickablePresentationProps;
    productNumbersContainer?: InjectableCss;
    actions?: ProductActionsStyle;
}

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    headerText: {
        weight: "bold",
        css: css`
            text-align: left;
            margin: 10px 0 5px 0;
        `,
    },
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;
        `,
    },
    focusedContainer: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;
            background-color: lightgray;
        `,
    },
    imageGridItem: {
        width: 3,
        css: css`
            max-width: 65px;
            max-height: 50px;
            flex-basis: 65px;
        `,
    },
    image: {
        width: "50px",
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    infoGridItem: {
        width: 9,
        css: css`
            flex-direction: column;
            max-width: calc(100% - 65px);
            flex-basis: calc(100% - 65px);
        `,
    },
    titleLink: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 100%;
                text-align: left;
                margin: 0;
            `,
        },
        css: css`
            width: 270px;
        `,
    },
    // erpNumberText: {
    //     ellipsis: true,
    //     css: css`
    //         width: 270px;
    //         text-align: left;
    //         margin: 5px 0 0 0;
    //     `,
    // },
};

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        const { products } = this.props;
        const styles = this.styles;
        return (
            <>
                <Typography
                    {...styles.headerText}
                    data-test-selector="autocompleteCategories_Header"
                    className="autocomplete-categories-header"
                >
                    {translate("Products")}&nbsp;&nbsp;({products.length})
                </Typography>
                {/** XNG-Changes */}
                {products.map(productContext => {
                    const {
                        product: {
                            id,
                            customerProductNumber,
                            productNumber,
                            manufacturerItem,
                            productTitle,
                            mediumImagePath,
                        },
                        productInfo: { productDetailPath },
                    } = productContext;

                    return (
                        <ProductContext.Provider value={productContext} key={id!}>
                            <StyledWrapper
                                {...(this.props.focusedItem?.id === id ? styles.focusedContainer : styles.container)}
                                className="autocomplete-products-container"
                            >
                                <Clickable href={productDetailPath} {...styles.imageClickable}>
                                    <LazyImage
                                        width="89"
                                        height="89"
                                        {...styles.image}
                                        src={mediumImagePath}
                                        className="autocomplete-products-image"
                                        altText={`autocomplete products for ${productTitle}`}
                                    />
                                </Clickable>
                                <StyledWrapper
                                    {...styles.infoGridItem}
                                    className="autocomplete-products-info-grid-item"
                                >
                                    <Link {...styles.titleLink} href={productDetailPath} aria-label={productTitle}>
                                        {productTitle}
                                    </Link>
                                    <StyledWrapper className="product-numbers" {...styles.productNumbersContainer}>
                                        <SmallHeadingAndText
                                            heading={customerProductNumber ? translate("My Part#") : translate("Part")}
                                            text={customerProductNumber || productNumber}
                                            extendedStyles={styles.erpNumberText}
                                        />
                                        <SmallHeadingAndText
                                            heading={translate("MFG")}
                                            text={manufacturerItem}
                                            extendedStyles={styles.erpNumberText}
                                        />
                                    </StyledWrapper>
                                    <ProductActions extendedStyles={styles.actions} secure />
                                </StyledWrapper>
                            </StyledWrapper>
                        </ProductContext.Provider>
                    );
                })}
            </>
        );
    }
}

export default AutocompleteProducts;
